import React from 'react';

interface IconProps {
  size?: number;
  className?: string;
}

// Design/UI Icon for "Pixel-perfect UIs"
export const DesignIcon: React.FC<IconProps> = ({ size = 32, className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
    <defs>
      <linearGradient id="designGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#6366f1" />
        <stop offset="100%" stopColor="#8b5cf6" />
      </linearGradient>
    </defs>
    {/* Artboard/Canvas */}
    <rect x="4" y="6" width="20" height="16" rx="2" stroke="url(#designGrad)" strokeWidth="1.5" fill="none"/>
    {/* Design elements */}
    <rect x="7" y="9" width="6" height="1.5" rx="0.75" fill="url(#designGrad)" opacity="0.8"/>
    <rect x="7" y="12" width="8" height="1" rx="0.5" fill="url(#designGrad)" opacity="0.6"/>
    <rect x="7" y="15" width="4" height="1" rx="0.5" fill="url(#designGrad)" opacity="0.6"/>
    {/* Color palette */}
    <circle cx="19" cy="11" r="1.5" fill="url(#designGrad)" opacity="0.9"/>
    <circle cx="19" cy="14" r="1.5" fill="#6366f1" opacity="0.7"/>
    <circle cx="19" cy="17" r="1.5" fill="#8b5cf6" opacity="0.7"/>
    {/* Tool cursor */}
    <path d="M26 4 L28 6 L27 7 L25 5 Z" fill="url(#designGrad)"/>
  </svg>
);

// Transformation Icon for "Complete Business Transformation" - Clean Design Style
export const TransformIcon: React.FC<IconProps> = ({ size = 32, className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
    <defs>
      <linearGradient id="transformGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#6366f1" />
        <stop offset="100%" stopColor="#8b5cf6" />
      </linearGradient>
    </defs>
    {/* Main container/workflow box */}
    <rect x="4" y="8" width="24" height="16" rx="2" stroke="url(#transformGrad)" strokeWidth="1.5" fill="none"/>
    {/* Input process (left side) */}
    <rect x="7" y="12" width="3" height="3" rx="0.5" fill="url(#transformGrad)" opacity="0.6"/>
    <rect x="7" y="17" width="3" height="3" rx="0.5" fill="url(#transformGrad)" opacity="0.6"/>
    {/* Transformation arrow */}
    <path d="M13 15.5 L19 15.5" stroke="url(#transformGrad)" strokeWidth="2" strokeLinecap="round"/>
    <path d="M17 13.5 L19 15.5 L17 17.5" stroke="url(#transformGrad)" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
    {/* Output/result (right side) */}
    <rect x="22" y="11" width="4" height="2" rx="1" fill="url(#transformGrad)" opacity="0.8"/>
    <rect x="22" y="14.5" width="4" height="1.5" rx="0.75" fill="url(#transformGrad)" opacity="0.8"/>
    <rect x="22" y="17.5" width="3" height="1.5" rx="0.75" fill="url(#transformGrad)" opacity="0.6"/>
  </svg>
);

// Scale/Growth Icon for "Scalable Solutions" - Clean Design Style
export const ScaleIcon: React.FC<IconProps> = ({ size = 32, className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
    <defs>
      <linearGradient id="scaleGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#6366f1" />
        <stop offset="100%" stopColor="#8b5cf6" />
      </linearGradient>
    </defs>
    {/* Dashboard/Analytics container */}
    <rect x="4" y="6" width="20" height="16" rx="2" stroke="url(#scaleGrad)" strokeWidth="1.5" fill="none"/>
    {/* Chart bars inside */}
    <rect x="7" y="17" width="2" height="3" rx="1" fill="url(#scaleGrad)" opacity="0.6"/>
    <rect x="10.5" y="15" width="2" height="5" rx="1" fill="url(#scaleGrad)" opacity="0.7"/>
    <rect x="14" y="12" width="2" height="8" rx="1" fill="url(#scaleGrad)" opacity="0.8"/>
    <rect x="17.5" y="10" width="2" height="10" rx="1" fill="url(#scaleGrad)"/>
    {/* Upward trend line */}
    <path d="M8 18 L11.5 16 L15 13 L18.5 11" stroke="url(#scaleGrad)" strokeWidth="1.2" fill="none" strokeLinecap="round"/>
    {/* Scale indicator - growth arrow */}
    <path d="M26 8 L28 6 L26 4" stroke="url(#scaleGrad)" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
    <line x1="28" y1="6" x2="28" y2="18" stroke="url(#scaleGrad)" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

// Support Icon for "24/7 Support & Maintenance" - Clean Design Style
export const SupportIcon: React.FC<IconProps> = ({ size = 32, className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
    <defs>
      <linearGradient id="supportGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#6366f1" />
        <stop offset="100%" stopColor="#8b5cf6" />
      </linearGradient>
    </defs>
    {/* Support dashboard/monitor */}
    <rect x="4" y="8" width="20" height="12" rx="2" stroke="url(#supportGrad)" strokeWidth="1.5" fill="none"/>
    {/* Status indicators */}
    <circle cx="8" cy="12" r="1" fill="url(#supportGrad)" opacity="0.8"/>
    <rect x="10.5" y="11.5" width="6" height="1" rx="0.5" fill="url(#supportGrad)" opacity="0.6"/>
    <circle cx="8" cy="16" r="1" fill="url(#supportGrad)" opacity="0.8"/>
    <rect x="10.5" y="15.5" width="4" height="1" rx="0.5" fill="url(#supportGrad)" opacity="0.6"/>
    {/* 24/7 indicator */}
    <rect x="17" y="11.5" width="5" height="2" rx="1" fill="url(#supportGrad)" opacity="0.2"/>
    <rect x="17.5" y="12" width="4" height="1" rx="0.5" fill="url(#supportGrad)" opacity="0.8"/>
    {/* Tools/settings icon */}
    <circle cx="26" cy="10" r="2" fill="none" stroke="url(#supportGrad)" strokeWidth="1.5"/>
    <circle cx="26" cy="10" r="0.8" fill="url(#supportGrad)" opacity="0.6"/>
  </svg>
);

// AI Icon for "AI-Assisted Development" - Clean Design Style
export const AIIcon: React.FC<IconProps> = ({ size = 32, className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
    <defs>
      <linearGradient id="aiGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#6366f1" />
        <stop offset="100%" stopColor="#8b5cf6" />
      </linearGradient>
    </defs>
    {/* Brain/Neural network core */}
    <circle cx="16" cy="16" r="8" stroke="url(#aiGrad)" strokeWidth="1.5" fill="none"/>
    {/* Neural nodes */}
    <circle cx="16" cy="12" r="1.5" fill="url(#aiGrad)" opacity="0.9"/>
    <circle cx="12" cy="16" r="1.5" fill="url(#aiGrad)" opacity="0.9"/>
    <circle cx="20" cy="16" r="1.5" fill="url(#aiGrad)" opacity="0.9"/>
    <circle cx="16" cy="20" r="1.5" fill="url(#aiGrad)" opacity="0.9"/>
    {/* Connections */}
    <path d="M16 12 L12 16 L16 20 L20 16 L16 12" stroke="url(#aiGrad)" strokeWidth="1" opacity="0.4" fill="none"/>
    {/* Sparkle/AI indicator top right */}
    <path d="M25 7 L25.5 8.5 L27 9 L25.5 9.5 L25 11 L24.5 9.5 L23 9 L24.5 8.5 Z" fill="url(#aiGrad)" opacity="0.8"/>
    {/* Sparkle/AI indicator bottom left */}
    <path d="M7 23 L7.5 24.5 L9 25 L7.5 25.5 L7 27 L6.5 25.5 L5 25 L6.5 24.5 Z" fill="url(#aiGrad)" opacity="0.6"/>
    {/* Code brackets */}
    <path d="M9 10 L7 12 L9 14" stroke="url(#aiGrad)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" opacity="0.5"/>
    <path d="M23 18 L25 20 L23 22" stroke="url(#aiGrad)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" opacity="0.5"/>
  </svg>
);

const FeatureIcons = {
  DesignIcon,
  TransformIcon,
  ScaleIcon,
  SupportIcon,
  AIIcon
};

export default FeatureIcons;